import { Link } from '@tanstack/react-router';

import { MoreVerticalIcon } from '@/react/components/icons/MoreVerticalIcon';
import { EditIcon } from '@/react/components/icons/EditIcon';
import { TrashIcon } from '@/react/components/icons/TrashIcon';
import { ActivityLogIcon } from '@/react/components/icons/ActivityLogIcon';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

import { EventOrderGenerateMenuItems } from '@components/EventManagement/sections/EventsToolbar/EventOrderGenerateMenuItems';

import { useTranslation } from '@hooks/useTranslation';

const DROPDOWN_ITEM_CLASS = cn(
  'relative isolate flex items-center gap-1.5 px-5 py-1.5 font-inter text-sm font-semibold text-off-black cursor-pointer',
  'hover:bg-transparent focus:bg-transparent data-[highlighted]:bg-transparent',
  'before:absolute before:inset-y-0 before:left-3 before:right-3 before:-z-10 before:rounded-xs',
  'hover:before:bg-dinnerplate-white-light data-[highlighted]:before:bg-dinnerplate-white-light'
);

export function EventActionsDropdown({
  editEventPath,
  historyPath,
  canWrite,
  canGenerate,
  eventTypeKey,
  productLine,
  eventStatus,
  onGenerate,
  disabled,
  onDelete,
}: {
  editEventPath: string;
  historyPath: string;
  // canWrite gates Edit + Delete (events.write); canGenerate gates the Generate
  // menu (event_orders.generate) so read-only fulfilment roles can print.
  canWrite: boolean;
  canGenerate: boolean;
  eventTypeKey: string;
  productLine?: string | null;
  eventStatus: string;
  onGenerate: (documentTypes: string[]) => void;
  disabled?: boolean;
  onDelete: () => void;
}) {
  const { t } = useTranslation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={disabled}
          aria-label={t('events_section.event_actions_label')}
          className={cn(
            'group size-[18px] min-w-0 p-0 rounded-xs',
            'hover:bg-dinnerplate-white',
            'data-[state=open]:bg-dinnerplate-white',
            'focus-visible:ring-2 focus-visible:ring-off-black focus-visible:ring-offset-2'
          )}
        >
          <MoreVerticalIcon aria-hidden="true" className="h-2.5 w-0.5 text-grey group-hover:text-off-black group-data-[state=open]:text-off-black" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[232px] px-0 py-3 rounded-lg border-[0.5px] border-dinnerplate-white bg-off-white shadow-soft">
        {canWrite && (
          <DropdownMenuItem asChild className={DROPDOWN_ITEM_CLASS}>
            <a href={editEventPath}>
              <EditIcon aria-hidden="true" className="size-5 shrink-0" />
              {t('events_section.dropdown_edit')}
            </a>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild className={DROPDOWN_ITEM_CLASS}>
          <Link to={historyPath}>
            <ActivityLogIcon aria-hidden="true" className="size-5 shrink-0" />
            {t('events_section.dropdown_history')}
          </Link>
        </DropdownMenuItem>
        {canGenerate && (
          <EventOrderGenerateMenuItems
            eventTypeKey={eventTypeKey}
            productLine={productLine}
            eventStatus={eventStatus}
            onGenerate={onGenerate}
            canGenerate={canGenerate}
          />
        )}
        {canWrite && (
          <>
            <DropdownMenuSeparator className="mx-2 my-3 bg-dinnerplate-white" />
            <DropdownMenuItem
              className={cn(DROPDOWN_ITEM_CLASS, 'text-chilli-red hover:before:bg-chilli-blush data-[highlighted]:before:bg-chilli-blush data-[highlighted]:text-chilli-red')}
              onSelect={onDelete}
            >
              <TrashIcon aria-hidden="true" className="size-3.5 shrink-0" />
              {t('events_section.dropdown_delete')}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
